import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeader } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL;

// Permission keys that are handled specially (not shown as checkboxes)
const DASHBOARD_KEYS = ['dashboard_full', 'dashboard_individual'];
const SCOPE_KEYS     = ['view_all_compsets'];

// Spanish labels for permission categories
const categoryLabels = {
  'Dashboard':     'Dashboard',
  'Data Entry':    'Ingreso de Datos',
  'Reports':       'Reportes',
  'Configuration': 'Configuración',
  'Data Scope':    'Alcance de Datos',
};

// Spanish labels for individual permissions
const permissionLabels = {
  'dashboard_full':       'Dashboard completo (todos los compsets)',
  'dashboard_individual': 'Dashboard individual (hotel propio)',
  'data_entry':           'Ingreso diario de datos',
  'ingreso_express':      'Ingreso Express',
  'retrospective':        'Ingreso Retrospectivo',
  'report_clasico':       'Reporte Clásico',
  'report_kpi':           'Reporte de KPIs',
  'config_users':         'Gestionar usuarios',
  'config_hotels':        'Gestionar hoteles',
  'config_compsets':      'Gestionar comp sets',
  'config_images':        'Gestionar imágenes',
  'config_roles':         'Gestionar papeles',
  'config_roles':         'Gestionar papeles',
  'view_all_compsets':    'Ver todos los comp sets del grupo',
};

function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPermissions, setFormPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // --- Derived special field values from formPermissions ---
  // Dashboard: 'none' | 'full' | 'individual'
  const dashboardValue = formPermissions.includes('dashboard_full')
    ? 'full'
    : formPermissions.includes('dashboard_individual')
    ? 'individual'
    : 'none';

  // Scope: 'all' | 'own'
  const scopeValue = formPermissions.includes('view_all_compsets') ? 'all' : 'own';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesRes, permsRes] = await Promise.all([
        fetch(`${API_URL}/api.php?path=roles`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api.php?path=permissions`, { headers: getAuthHeader() }),
      ]);
      const rolesData = await rolesRes.json();
      const permsData = await permsRes.json();
      if (rolesData.success) setRoles(rolesData.data);
      else setError('Error al cargar los papeles.');
      if (permsData.success) setAllPermissions(permsData.data);
      else setError('Error al cargar los permisos.');
    } catch (e) {
      setError('Error de red al cargar los datos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setFormName('');
    setFormDescription('');
    setFormPermissions([]);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setFormName(role.name);
    setFormDescription(role.description || '');
    setFormPermissions([...role.permissions]);
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setError('');
  };

  // Toggle a regular checkbox permission
  const togglePermission = (key) => {
    setFormPermissions(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Handle dashboard radio change
  const handleDashboardChange = (value) => {
    setFormPermissions(prev => {
      const filtered = prev.filter(k => !DASHBOARD_KEYS.includes(k));
      if (value === 'full')       return [...filtered, 'dashboard_full'];
      if (value === 'individual') return [...filtered, 'dashboard_individual'];
      return filtered; // 'none'
    });
  };

  // Handle scope radio change
  const handleScopeChange = (value) => {
    setFormPermissions(prev => {
      const filtered = prev.filter(k => !SCOPE_KEYS.includes(k));
      if (value === 'all') return [...filtered, 'view_all_compsets'];
      return filtered; // 'own' — no permission key needed
    });
  };

  const handleSave = async () => {
    if (!formName.trim()) { setError('El nombre del papel es obligatorio.'); return; }
    setSaving(true);
    setError('');

    const isEdit  = !!editingRole;
    const url     = isEdit
      ? `${API_URL}/api.php?path=roles/${editingRole.role_id}`
      : `${API_URL}/api.php?path=roles`;
    const method  = isEdit ? 'PUT' : 'POST';

    try {
      const res  = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          name:        formName.trim(),
          description: formDescription.trim(),
          permissions: formPermissions,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess(isEdit ? 'Papel actualizado correctamente.' : 'Papel creado correctamente.');
        closeModal();
        fetchData();
      } else {
        setError(data.error?.message || 'Error al guardar el papel.');
      }
    } catch (e) {
      setError('Error de red al guardar el papel.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const res  = await fetch(`${API_URL}/api.php?path=roles/${deleteTarget.role_id}`, {
        method: 'DELETE', headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess('Papel eliminado correctamente.');
        fetchData();
      } else {
        setError(data.error?.message || 'Error al eliminar el papel.');
      }
    } catch (e) {
      setError('Error de red al eliminar el papel.');
    } finally {
      setDeleteTarget(null);
    }
  };

  // Build a readable summary of a role's permissions for the table
  const buildPermissionSummary = (perms) => {
    return perms.map(p => permissionLabels[p] || p);
  };

  if (loading) return <div style={styles.page}><p>Cargando papeles...</p></div>;

  // Get all checkbox permissions (excluding special ones handled separately)
  const checkboxCategories = Object.entries(allPermissions).reduce((acc, [cat, perms]) => {
    const filtered = perms.filter(p => !DASHBOARD_KEYS.includes(p.key) && !SCOPE_KEYS.includes(p.key));
    if (filtered.length > 0) acc[cat] = filtered;
    return acc;
  }, {});

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Gestión de Papeles</h2>
          <p style={styles.subtitle}>
            Defina los papeles del sistema y los permisos que tiene cada uno.
            Asigne un papel a cada usuario desde la sección de Usuarios.
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={openCreateModal}>+ Nuevo Papel</button>
      </div>

      {successMessage && <div style={styles.success}>{successMessage}</div>}
      {error && !showModal && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Papel</th>
              <th style={styles.th}>Descripción</th>
              <th style={styles.th}>Permisos asignados</th>
              <th style={styles.th}>Usuarios</th>
              <th style={styles.th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => (
              <tr key={role.role_id} style={styles.tr}>
                <td style={styles.td}><strong>{role.name}</strong></td>
                <td style={styles.td}>{role.description || '—'}</td>
                <td style={styles.td}>
                  <div style={styles.permTags}>
                    {buildPermissionSummary(role.permissions).map((label, i) => (
                      <span key={i} style={styles.tag}>{label}</span>
                    ))}
                    {role.permissions.length === 0 && (
                      <span style={styles.none}>Sin permisos asignados</span>
                    )}
                  </div>
                </td>
                <td style={{...styles.td, textAlign: 'center'}}>
                  {role.user_count} {role.user_count === 1 ? 'usuario' : 'usuarios'}
                </td>
                <td style={styles.td}>
                  <button style={styles.btnEdit} onClick={() => openEditModal(role)}>Editar</button>
                  <button style={styles.btnDelete} onClick={() => setDeleteTarget(role)}>Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              {editingRole ? `Editar Papel: ${editingRole.name}` : 'Crear Nuevo Papel'}
            </h3>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.formGroup}>
              <label style={styles.label}>Nombre del papel <span style={styles.required}>*</span></label>
              <input style={styles.input} value={formName} onChange={e => setFormName(e.target.value)} placeholder="Ej: Gerente Regional" />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Descripción</label>
              <input style={styles.input} value={formDescription} onChange={e => setFormDescription(e.target.value)} placeholder="Descripción opcional del papel" />
            </div>

            {/* --- DASHBOARD (Radio buttons) --- */}
            <div style={styles.specialSection}>
              <div style={styles.specialTitle}>Dashboard</div>
              <p style={styles.permHint}>Selecciona qué tipo de dashboard puede ver este papel.</p>
              {[
                { value: 'none',       label: 'Sin acceso al dashboard' },
                { value: 'full',       label: 'Dashboard completo — todos los compsets', key: 'dashboard_full' },
                { value: 'individual', label: 'Dashboard individual — solo hotel propio', key: 'dashboard_individual' },
              ].map(opt => (
                <label key={opt.value} style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="dashboard"
                    value={opt.value}
                    checked={dashboardValue === opt.value}
                    onChange={() => handleDashboardChange(opt.value)}
                    style={styles.radio}
                  />
                  <span>
                    {opt.label}
                    {opt.key && <span style={styles.permKey}> ({opt.key})</span>}
                  </span>
                </label>
              ))}
            </div>

            {/* --- SCOPE (Radio buttons) --- */}
            <div style={styles.specialSection}>
              <div style={styles.specialTitle}>Alcance de Datos</div>
              <p style={styles.permHint}>Define qué compsets puede ver este papel en reportes, ingreso de datos y otras secciones.</p>
              {[
                { value: 'all', label: 'Ver todos los comp sets del grupo', key: 'view_all_compsets' },
                { value: 'own', label: 'Ver solo el comp set asignado al usuario' },
              ].map(opt => (
                <label key={opt.value} style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="scope"
                    value={opt.value}
                    checked={scopeValue === opt.value}
                    onChange={() => handleScopeChange(opt.value)}
                    style={styles.radio}
                  />
                  <span>
                    {opt.label}
                    {opt.key && <span style={styles.permKey}> ({opt.key})</span>}
                  </span>
                </label>
              ))}
            </div>

            {/* --- CHECKBOX PERMISSIONS --- */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Permisos adicionales</label>
              <p style={styles.permHint}>
                Seleccione las secciones y funciones a las que tendrá acceso este papel.
              </p>
              {Object.entries(checkboxCategories).map(([category, perms]) => (
                <div key={category} style={styles.permGroup}>
                  <div style={styles.permCategory}>{categoryLabels[category] || category}</div>
                  {perms.map(p => (
                    <label key={p.key} style={styles.checkLabel}>
                      <input
                        type="checkbox"
                        checked={formPermissions.includes(p.key)}
                        onChange={() => togglePermission(p.key)}
                        style={styles.checkbox}
                      />
                      <span>
                        <strong>{permissionLabels[p.key] || p.label}</strong>
                        <span style={styles.permKey}> ({p.key})</span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={closeModal} disabled={saving}>Cancelar</button>
              <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Papel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div style={styles.overlay}>
          <div style={{...styles.modal, maxWidth: '440px'}}>
            <h3 style={styles.modalTitle}>Confirmar eliminación</h3>
            {deleteTarget.user_count > 0 ? (
              <>
                <div style={styles.errorBox}><strong>No es posible eliminar este papel.</strong></div>
                <p style={{marginTop: '12px'}}>
                  El papel <strong>{deleteTarget.name}</strong> tiene{' '}
                  <strong>{deleteTarget.user_count} usuario(s)</strong> asignados.
                  Reasigne esos usuarios a otro papel antes de eliminar éste.
                </p>
              </>
            ) : (
              <p>
                ¿Está seguro de que desea eliminar el papel <strong>{deleteTarget.name}</strong>?
                Esta acción no se puede deshacer.
              </p>
            )}
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setDeleteTarget(null)}>Cancelar</button>
              {deleteTarget.user_count === 0 && (
                <button style={styles.btnDelete} onClick={handleDeleteConfirm}>Sí, eliminar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page:           { padding: '24px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'sans-serif' },
  header:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '16px' },
  title:          { margin: '0 0 6px', fontSize: '22px', color: '#1a1a2e' },
  subtitle:       { margin: 0, fontSize: '13px', color: '#666', maxWidth: '600px' },
  success:        { background: '#d4edda', color: '#155724', padding: '10px 16px', borderRadius: '6px', marginBottom: '16px' },
  errorBox:       { background: '#f8d7da', color: '#721c24', padding: '10px 16px', borderRadius: '6px', marginBottom: '16px' },
  tableWrap:      { overflowX: 'auto' },
  table:          { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  th:             { background: '#1a1a2e', color: '#fff', padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px' },
  tr:             { borderBottom: '1px solid #e9ecef' },
  td:             { padding: '12px 16px', fontSize: '14px', verticalAlign: 'top' },
  permTags:       { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  tag:            { background: '#e8f4fd', color: '#1a6fa0', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  none:           { color: '#aaa', fontSize: '13px', fontStyle: 'italic' },
  btnPrimary:     { background: '#1a6fa0', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' },
  btnSecondary:   { background: '#6c757d', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  btnEdit:        { background: '#f0ad4e', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', marginRight: '6px', fontSize: '13px' },
  btnDelete:      { background: '#dc3545', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  overlay:        { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:          { background: '#fff', borderRadius: '10px', padding: '28px', width: '90%', maxWidth: '580px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle:     { margin: '0 0 20px', fontSize: '18px', color: '#1a1a2e' },
  modalActions:   { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', borderTop: '1px solid #eee', paddingTop: '16px' },
  formGroup:      { marginBottom: '20px' },
  label:          { display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px', color: '#333' },
  required:       { color: '#dc3545' },
  input:          { width: '100%', padding: '9px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  permHint:       { fontSize: '13px', color: '#666', margin: '0 0 12px' },
  permGroup:      { marginBottom: '16px', background: '#f8f9fa', borderRadius: '6px', padding: '12px' },
  permCategory:   { fontWeight: '700', fontSize: '12px', color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #dee2e6' },
  checkLabel:     { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', marginBottom: '8px', cursor: 'pointer', lineHeight: '1.4' },
  checkbox:       { width: '16px', height: '16px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 },
  permKey:        { color: '#999', fontSize: '11px' },
  specialSection: { background: '#f0f4f8', border: '1px solid #d0dce8', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' },
  specialTitle:   { fontWeight: '700', fontSize: '13px', color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #c5d5e5' },
  radioLabel:     { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', marginBottom: '8px', cursor: 'pointer', lineHeight: '1.4' },
  radio:          { width: '16px', height: '16px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 },
};

export default RoleManagement;